import { useEffect, useState } from "react";

/**
 * Light / dark appearance, entirely client-side.
 *
 * The preference lives in localStorage (no backend involvement) and is applied
 * as `data-theme="light" | "dark"` on <html>; index.css redefines the neutral
 * palette under `:root[data-theme="dark"]`. "system" follows the OS setting
 * and tracks changes live via matchMedia.
 */
export const THEME_KEY = "np_theme";
export const THEME_OPTIONS = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const VALID = new Set(THEME_OPTIONS.map((o) => o.value));

const systemQuery = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

export const getThemePreference = () => {
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    return VALID.has(stored) ? stored : "system";
  } catch (e) {
    return "system";
  }
};

const resolve = (pref) => {
  if (pref === "light" || pref === "dark") return pref;
  return systemQuery()?.matches ? "dark" : "light";
};

const paint = (pref) => {
  document.documentElement.dataset.theme = resolve(pref);
};

let listening = false;

/** Apply the stored preference. Call once before first render to avoid a flash. */
export const applyThemePreference = () => {
  paint(getThemePreference());
  const mq = systemQuery();
  if (mq && !listening) {
    listening = true;
    const onChange = () => {
      if (getThemePreference() === "system") paint("system");
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }
};

export const setThemePreference = (pref) => {
  const next = VALID.has(pref) ? pref : "system";
  try {
    window.localStorage.setItem(THEME_KEY, next);
  } catch (e) {
    // Private mode / storage blocked: still apply for this session.
  }
  paint(next);
  return next;
};

/** React hook: [preference, setPreference]. */
export const useThemePreference = () => {
  const [pref, setPref] = useState(getThemePreference);
  useEffect(() => {
    applyThemePreference();
  }, []);
  const update = (next) => setPref(setThemePreference(next));
  return [pref, update];
};
